import { useEffect, useState } from "react";
import { Share2 } from "lucide-react";
import { useLocation, useParams } from "react-router-dom";
import { Link, useNavigate } from "../app/navigation.js";
import { rankLabel, type Seat } from "@guandan/engine";
import type { MatchSummary } from "@guandan/protocol";
import { Avatar } from "../art/Avatar.js";
import { CardFace } from "../art/Card.js";
import { mobileCopy } from "../app/mobileCopy.js";
import { useLocale } from "../i18n.js";
import {
  api,
  fetchReplayCard,
  type PublicPlayer,
  type ReplayCard,
} from "../net/api.js";
import { gameError } from "../net/game-errors.js";
import { refreshSession, useSession } from "../net/session.js";
import {
  Button,
  EmptyState,
  PageHeader,
  Panel,
  Skeleton,
} from "../ui/primitives.js";
import "./mobile-screens.css";
import { HandRewardHistory } from "./HandRewards.js";
import { MatchSettlement } from "./Table.js";
import { isWidePath } from "../app/widePath.js";

type SummaryResponse = Awaited<ReturnType<typeof api.matchSummary>>;

export function ResultScreen({ wide = false }: { wide?: boolean }) {
  const { locale } = useLocale();
  const c = (
    key: Parameters<typeof mobileCopy>[1],
    params?: Record<string, string | number>,
  ) => mobileCopy(locale, key, params);
  const { roomId = "" } = useParams();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const wideResult = wide || isWidePath(routerLocation.pathname);
  const session = useSession();
  const me = session.status === "signedIn" ? session.me.user : null;
  const [result, setResult] = useState<SummaryResponse | null>(null);
  const [card, setCard] = useState<ReplayCard | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<Record<string, PublicPlayer>>({});
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [shareState, setShareState] = useState<"idle" | "working" | "shared">(
    "idle",
  );
  const [following, setFollowing] = useState<
    Record<string, "working" | "following" | "friends">
  >({});
  const [showLevelReward, setShowLevelReward] = useState(false);
  const [acknowledgingReward, setAcknowledgingReward] = useState(false);

  const currentCardPlayer =
    card?.players.find((player) => player.userId === me?.id) ??
    card?.players[0];
  const currentStats = result?.summary.players.find(
    (player) => player.seat === currentCardPlayer?.seat,
  );
  const settlement = currentStats?.settlement;

  useEffect(() => {
    let attempts = 0;
    let timer = 0;
    const load = () => {
      void Promise.all([
        api.matchSummary(roomId),
        fetchReplayCard(roomId, false),
      ])
        .then(([summary, replayCard]) => {
          if (replayCard === null) throw new Error("report_not_ready");
          setResult(summary);
          setCard(replayCard);
          void refreshSession();
        })
        .catch(() => {
          attempts += 1;
          if (attempts < 6) timer = window.setTimeout(load, 1500);
          else setFailed(true);
        });
    };
    load();
    return () => window.clearTimeout(timer);
  }, [roomId]);

  useEffect(() => {
    if (card === null) return;
    const others = card.players.filter((player) => player.userId !== me?.id);
    void Promise.all(
      others.map((player) =>
        api
          .player(player.userId)
          .then((profile) => [player.userId, profile] as const)
          .catch(() => null),
      ),
    ).then((rows) => {
      setProfiles(
        Object.fromEntries(
          rows.filter(
            (row): row is readonly [string, PublicPlayer] => row !== null,
          ),
        ),
      );
    });
  }, [card, me?.id]);

  useEffect(() => {
    const notificationId = settlement?.notificationId;
    if (
      settlement === undefined ||
      notificationId === undefined ||
      settlement.levelUps.length === 0
    )
      return;
    let active = true;
    // The level-up now arrives as a channel event rather than a notification
    // row; the settlement receipt's id is that event's id.
    void api
      .events()
      .then(({ events }) => {
        const event = events.find((item) => item.id === notificationId);
        if (active && event?.readAt === null) setShowLevelReward(true);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [settlement?.notificationId, settlement?.levelUps.length]);

  if (failed) {
    return (
      <div className="screen-stack detail-screen">
        <PageHeader title={c("result")} backTo="/" />
        <EmptyState
          title={c("reportPreparing")}
          hint={c("reportPreparingHint")}
          action={
            <Button onClick={() => location.reload()}>{c("retry")}</Button>
          }
        />
      </div>
    );
  }
  if (result === null || card === null) return <Skeleton lines={7} />;

  const myStats = currentStats;
  const won = myStats?.team === result.summary.winner;
  const winningPlayers = card.players.filter(
    (player) => player.team === result.summary.winner,
  );
  const highlight =
    result.summary.moments.find((moment) => moment.kind === "bomb") ??
    result.summary.moments.find((moment) => moment.kind === "firstOut") ??
    result.summary.moments[0] ??
    null;

  const playAgain = async () => {
    if (busy) return;
    setActionError(null);
    setBusy(true);
    try {
      if (result.mode === "ranked") {
        await api.joinQueue({ mode: "ranked", rulesetId: result.rulesetId });
        navigate("/");
      } else {
        const next = await api.rematch(roomId);
        navigate(`/room/${next.code}`);
      }
    } catch (caught) {
      setActionError(gameError(caught, locale));
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    setShareState("working");
    try {
      const shared = await api.shareReplay(roomId);
      const url = new URL(shared.url, window.location.origin).toString();
      if (navigator.share !== undefined) {
        await navigator.share({
          title: c("shareResultTitle"),
          text: c("shareResultText"),
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
      }
      setShareState("shared");
    } catch (caught) {
      if (!(caught instanceof DOMException && caught.name === "AbortError")) setActionError(c("couldNotLoad"));
      setShareState("idle");
    }
  };

  const addFriend = async (userId: string) => {
    setFollowing((current) => ({ ...current, [userId]: "working" }));
    try {
      const relationship = await api.follow(userId);
      setFollowing((current) => ({
        ...current,
        [userId]: relationship.friends ? "friends" : "following",
      }));
    } catch {
      setFollowing((current) => {
        const next = { ...current };
        delete next[userId];
        return next;
      });
    }
  };

  const acknowledgeLevelReward = async () => {
    const notificationId = settlement?.notificationId;
    if (notificationId === undefined) return;
    setAcknowledgingReward(true);
    try {
      await api.readEvent(notificationId);
      setShowLevelReward(false);
      await refreshSession();
    } finally {
      setAcknowledgingReward(false);
    }
  };

  if (wideResult) return <MatchSettlement summary={result.summary} room={{ roomId: card.roomId, seats: card.players }}
    seat={(currentCardPlayer?.seat as Seat | undefined) ?? null}
    onLobby={() => navigate("/", { replace: true })} page
    onRematch={() => void playAgain()} onReplay={() => navigate(`/record/replay/${roomId}`)} busy={busy} error={actionError} />;

  return (
    <div className="screen-stack detail-screen result-page">
      {actionError && <p role="alert" className="error-note">{actionError}</p>}
      <PageHeader
        title={c("result")}
        backTo="/"
        action={
          <button
            className="result-share-button"
            type="button"
            aria-label={c("shareResult")}
            onClick={() => void share()}
            disabled={shareState === "working"}
          >
            <Share2 aria-hidden="true" />
          </button>
        }
      />

      <Panel className={`result-hero ${won ? "result-win" : "result-loss"}`}>
        <div className="result-team-avatars" aria-label={c("winningTeam")}>
          {winningPlayers.map((player) => (
            <Avatar
              key={player.userId}
              avatarId={player.avatarId}
              expression="win"
              size={58}
              title={player.name}
            />
          ))}
          <span aria-hidden="true">✦</span>
        </div>
        <div className="result-hero-copy">
          <small>{result.mode === "ranked" ? c("ranked") : c("gold")}</small>
          <h2>{won ? c("youWon") : c("matchComplete")}</h2>
          <p>
            {result.summary.winner === 0 ? c("teamAWins") : c("teamBWins")} ·{" "}
            {c("matchMeta", {
              hands: result.summary.handsPlayed,
              minutes: Math.max(
                1,
                Math.round(result.summary.durationMs / 60000),
              ),
            })}
          </p>
        </div>
        <div className="reward-grid" aria-label={c("yourRewards")}>
          <Reward
            value={signed(settlement?.coins.net ?? myStats?.reward.coins ?? 0)}
            label={c("coins")}
          />
          <Reward
            value={signed(myStats?.reward.xp ?? 0)}
            label={c("xpEarned")}
          />
          <Reward
            value={signed(myStats?.reward.ratingDelta ?? 0)}
            label={c("rating")}
          />
        </div>
      </Panel>

      {myStats?.handSettlements && <HandRewardHistory hands={myStats.handSettlements} />}
      {settlement && (
        <div className="settlement-breakdowns">
          <SettlementBreakdown title={c("xpBreakdown")}>
            <RewardLine label={c("baseXp")} value={settlement.xp.raw} />
            <RewardLine
              label={c("participation")}
              value={`${Math.round(settlement.participation.participationRate * 100)}%`}
            />
            <RewardLine
              label={c("multiplier")}
              value={`×${settlement.xp.participationMultiplier}`}
            />
            <RewardLine label={c("xpEarned")} value={signed(settlement.xp.total)} strong />
          </SettlementBreakdown>
          <SettlementBreakdown title={c("coinBreakdown")}>
            <RewardLine label={c("entry")} value={signed(-settlement.coins.entry)} />
            <RewardLine label={c("payout")} value={signed(settlement.coins.payout)} />
            <RewardLine
              label={c("accountBonus")}
              value={signed(
                settlement.coins.firstWin +
                  settlement.coins.weekly +
                  settlement.coins.levelUp,
              )}
            />
            <RewardLine label={c("coins")} value={signed(settlement.coins.net)} strong />
          </SettlementBreakdown>
          {settlement.rating && (
            <SettlementBreakdown title={c("eloBreakdown")}>
              <RewardLine label={c("baseElo")} value={signedDecimal(settlement.rating.baseDelta)} />
              <RewardLine
                label={c("performanceCorrection")}
                value={signedDecimal(settlement.rating.performanceDelta)}
              />
              <RewardLine
                label={c("disconnectCorrection")}
                value={signed(settlement.rating.disconnectAdjustment)}
              />
              <RewardLine label={c("rating")} value={signed(settlement.rating.delta)} strong />
            </SettlementBreakdown>
          )}
        </div>
      )}

      <div className="result-primary-actions">
        <Button
          variant="primary"
          size="lg"
          busy={busy}
          onClick={() => void playAgain()}
        >
          {c("rematch")}
        </Button>
        <Button onClick={() => navigate("/", { replace: true })}>
          {c("backHome")}
        </Button>
      </div>

      {highlight !== null && (
        <section
          className="result-section"
          aria-labelledby="result-highlight-title"
        >
          <h2 id="result-highlight-title">{c("matchHighlight")}</h2>
          <ResultHighlight
            moment={highlight}
            summary={result.summary}
            card={card}
            c={c}
          />
        </section>
      )}

      <section
        className="result-section"
        aria-labelledby="result-players-title"
      >
        <h2 id="result-players-title">{c("playersAtTable")}</h2>
        <div className="result-player-list">
          {card.players.map((player) => {
            const stats = result.summary.players.find(
              (item) => item.seat === player.seat,
            );
            const profile = profiles[player.userId];
            const isMe = player.userId === me?.id;
            const followState = following[player.userId];
            const relationshipLabel =
              followState === "friends" || profile?.friends
                ? c("alreadyFriends")
                : followState === "following" || profile?.following
                  ? c("friendRequestSent")
                  : c("addFriend");
            return (
              <Panel
                className={`result-player-row team-${player.team}`}
                key={player.userId}
              >
                <Link
                  className="result-player-identity"
                  to={`/player/${player.userId}`}
                  state={{ from: `/result/${roomId}` }}
                >
                  <Avatar
                    avatarId={player.avatarId}
                    expression={stats?.labels.includes("mvp") ? "win" : "idle"}
                    size={44}
                    title={player.name}
                  />
                  <span>
                    <b>
                      {player.name} {isMe && <em>{c("guideYou")}</em>}
                    </b>
                    <small>
                      {player.team === 0 ? c("teamA") : c("teamB")}
                      {stats?.labels.length
                        ? ` · ${stats.labels
                            .map((label) => c(resultLabel(label)))
                            .join(" · ")}`
                        : ""}
                    </small>
                  </span>
                </Link>
                <div className="result-player-stats">
                  <span>
                    <b>{stats?.firstOuts ?? 0}</b>
                    <small>{c("firstOuts")}</small>
                  </span>
                  <span>
                    <b>{stats?.trickWins ?? 0}</b>
                    <small>{c("trickWins")}</small>
                  </span>
                  <span>
                    <b>{stats?.bombs ?? 0}</b>
                    <small>{c("bombCount")}</small>
                  </span>
                </div>
                {!isMe && profile?.canFollow && (
                  <Button
                    size="sm"
                    variant="secondary"
                    busy={followState === "working"}
                    disabled={relationshipLabel !== c("addFriend")}
                    onClick={() => void addFriend(player.userId)}
                  >
                    {relationshipLabel}
                  </Button>
                )}
              </Panel>
            );
          })}
        </div>
      </section>

      <section className="result-section" aria-labelledby="result-hands-title">
        <div className="section-heading">
          <h2 id="result-hands-title">{c("handHistory")}</h2>
          <Link to={`/record/replay/${roomId}`}>{c("watchReplay")}</Link>
        </div>
        <div className="result-hand-track">
          {result.summary.hands.map((hand) => (
            <Panel className="result-hand-card" key={hand.handNumber}>
              <span>{c("handNumber", { number: hand.handNumber })}</span>
              <ResultTeamFaces
                players={card.players.filter(
                  (player) => player.team === hand.winningTeam,
                )}
              />
              <b>{hand.winningTeam === 0 ? c("teamA") : c("teamB")}</b>
              <strong>+{hand.upgrade}</strong>
              <small>
                {rankLabel(hand.level)} →{" "}
                {rankLabel(levelAfter(result.summary, hand.handNumber))}
              </small>
            </Panel>
          ))}
        </div>
      </section>

      <Panel className="result-share-card">
        <span aria-hidden="true"><Share2 /></span>
        <div>
          <h3>{c("shareResult")}</h3>
          <p>{c("shareResultHint")}</p>
        </div>
        <Button
          size="sm"
          variant="secondary"
          busy={shareState === "working"}
          onClick={() => void share()}
        >
          {shareState === "shared" ? c("shareDone") : c("share")}
        </Button>
      </Panel>

      {showLevelReward && settlement && (
        <div className="level-reward-backdrop" role="presentation">
          <Panel
            className="level-reward-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="level-reward-title"
          >
            <span className="level-reward-icon" aria-hidden="true">🎉</span>
            <h2 id="level-reward-title">{c("levelUpTitle")}</h2>
            {settlement.levelUps.map((item) => (
              <p key={item.level}>
                {c("levelUpBody", { level: item.level, coins: item.coins })}
              </p>
            ))}
            <strong>
              +{settlement.levelUps.reduce((sum, item) => sum + item.coins, 0)} {c("levelUpCoins")}
            </strong>
            <Button
              variant="primary"
              busy={acknowledgingReward}
              onClick={() => void acknowledgeLevelReward()}
            >
              {c("dismiss")}
            </Button>
          </Panel>
        </div>
      )}
    </div>
  );
}

function SettlementBreakdown({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Panel className="settlement-breakdown">
      <h3>{title}</h3>
      <dl>{children}</dl>
    </Panel>
  );
}

function RewardLine({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string | number;
  strong?: boolean;
}) {
  return (
    <div className={strong ? "total" : ""}>
      <dt>{label}</dt>
      <dd className="tabular">{value}</dd>
    </div>
  );
}

function Reward({ value, label }: { value: string | number; label: string }) {
  return (
    <span>
      <b className="tabular">{value}</b>
      <small>{label}</small>
    </span>
  );
}

function ResultTeamFaces({ players }: { players: ReplayCard["players"] }) {
  return (
    <span className="result-mini-team" aria-hidden="true">
      {players.slice(0, 2).map((player) => (
        <Avatar key={player.userId} avatarId={player.avatarId} size={28} />
      ))}
    </span>
  );
}

function ResultHighlight({
  moment,
  summary,
  card,
  c,
}: {
  moment: MatchSummary["moments"][number];
  summary: MatchSummary;
  card: ReplayCard;
  c: (
    key: Parameters<typeof mobileCopy>[1],
    params?: Record<string, string | number>,
  ) => string;
}) {
  const player = card.players.find((item) => item.seat === moment.seat);
  const level =
    summary.hands.find((hand) => hand.handNumber === moment.handNumber)
      ?.level ?? 2;
  const title =
    moment.kind === "bomb"
      ? c("highlightBomb", { name: player?.name ?? c("player") })
      : moment.kind === "firstOut"
        ? c("highlightFirst", { name: player?.name ?? c("player") })
        : c("highlightTeam", {
            team: moment.winningTeam === 0 ? "A" : "B",
          });
  return (
    <Panel className="result-highlight-card">
      <Avatar
        avatarId={player?.avatarId ?? card.players[0]?.avatarId ?? "avatar-01"}
        expression="win"
        size={54}
        title={player?.name ?? c("player")}
      />
      <div>
        <small>{c("handNumber", { number: moment.handNumber })}</small>
        <h3>{title}</h3>
        <p>
          {c(
            moment.kind === "bomb"
              ? "highlightBombHint"
              : "highlightFinishHint",
          )}
        </p>
      </div>
      {moment.cards.length > 0 && (
        <div className="result-highlight-cards" aria-hidden="true">
          {moment.cards.slice(0, 6).map((item, index) => (
            <CardFace
              key={`${item}-${index}`}
              card={item}
              level={level}
              width={30}
              height={42}
            />
          ))}
        </div>
      )}
    </Panel>
  );
}

function levelAfter(summary: MatchSummary, handNumber: number): number {
  const hand = summary.hands.find((item) => item.handNumber === handNumber);
  if (hand === undefined) return 2;
  if (hand.droppedToLevel !== null) return hand.droppedToLevel;
  const next = summary.hands.find((item) => item.handNumber === handNumber + 1);
  return next?.level ?? summary.levels[hand.winningTeam];
}

function resultLabel(label: MatchSummary["players"][number]["labels"][number]) {
  return {
    mvp: "resultMvp",
    controller: "resultController",
    bomber: "resultBomber",
    sprinter: "resultSprinter",
  }[label] as
    | "resultMvp"
    | "resultController"
    | "resultBomber"
    | "resultSprinter";
}

function signed(value: number): string {
  return `${value >= 0 ? "+" : ""}${value}`;
}

function signedDecimal(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return `${rounded >= 0 ? "+" : ""}${rounded.toFixed(1)}`;
}
